"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Avatar,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  IconButton,
  LinearProgress,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  FormLabel,
  TextareaAutosize,
} from "@mui/material"
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { useLocation, useHistory } from "react-router-dom"
import { IonPage, IonContent } from "@ionic/react"
import { 
  getJobApplicantByJobRole, 
  updateSelectedApplicants,
} from "../../../api/projects/project"
import { formatToNaira } from "../../../common/utils_t"
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

interface Applicant {
  id: number
  full_name: string
  email_address: string
  phone_number: string
  date_of_birth: string
  gender: string
  nationality: string
  location_city: string
  location_state_region: string
  location_country: string
  location_area: string
  highest_education_level: string
  total_years_experience: string
  specific_roles_experience: string
  job_functions: string
  employment_type_preferences: string
  preferred_industries: string
  preferred_job_titles: string
  salary_expectation_min: string
  salary_expectation_max: string
  languages_spoken: string
  availability: string
  remote_work_preference: number
  willing_to_relocate: number
  work_authorization_status: string
  employed_status: number
  interviewed_status: number
  screened_status: number
  unscreened_status: number
  rejected_status: number
  project_id: string
  job_roleID: string
  created_at: string
  updated_at: string
  user_id: number
  cv_file?: string
}

type StatusTab = "all" | "screened" | "unscreened" | "interviewed" | "employed" | "rejected"

interface InterviewDetails {
  interviewType: "physical" | "virtual"
  address: string
  zoomLink: string
  standardHours: {
    mondayToFriday: string
    break: string
    saturday: string
  }
  flexibility: string
  workingConditions: string
  interviewDate: string
  interviewTime: string
  contactInfo: string
  recommendation: string
  warmRegards: string
  jobTitle: string
  reportsTo: string
  jobSummary: string
  keyResponsibilities: string
  payRange: string
}

const ATSJobApplicants: React.FC = () => {
  const location = useLocation()
  const history = useHistory()
  const segment = location.pathname.split("/")
  const projectId = segment[3]
  const job_roleID = segment[4]

  const [applicants, setApplicants] = useState<Applicant[]>([])
  const [selectedApplicants, setSelectedApplicants] = useState<number[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [statusTab, setStatusTab] = useState<StatusTab>("unscreened")
  const [filters, setFilters] = useState({
    education: "",
    location_state: "",
    location_city: "",
    nationality: "",
    gender: "",
    employment_type: "",
    salary_min: "",
    salary_max: "",
    remote_work: "",
    willing_to_relocate: "",
    preferred_industries: "",
    availability: "",
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null)
  const [showApplicantModal, setShowApplicantModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  })

  const [showCVPreview, setShowCVPreview] = useState(false);
  const [previewCVFile, setPreviewCVFile] = useState('');

  // Interview scheduling states
  const [showInterviewDialog, setShowInterviewDialog] = useState(false)
  const [interviewDetails, setInterviewDetails] = useState<InterviewDetails>({
    interviewType: "physical",
    address: "EC Concept, 11A, Patrick Ehimen, Off First Ugbor, GRA, Benin City (Use maps for easy navigation).",
    zoomLink: "",
    standardHours: {
      mondayToFriday: "8:00 AM – 6:00 PM (or 9:00 AM – 7:00 PM)",
      break: "1 hour lunch break",
      saturday: "Half-day, usually 9:00 AM – 3:00 PM, depending on studio workload or shoot schedules."
    },
    flexibility: `Because media firms often have irregular production times, the secretary may occasionally:
• Work overtime during big projects or shoots.
• Be required to come in early or stay late for client sessions or set preparations.
• Have time-off in lieu for extra hours worked.`,
    workingConditions: `• Full-time, with occasional weekend or evening work depending on studio schedules or productions.
• Fast-paced, creative work environment.`,
    interviewDate: "Thursday, 27th November 2025",
    interviewTime: "10:00 AM",
    contactInfo: "+2348038654275",
    recommendation: "Kindly go along with a copy of your CV and confirm your availability for the interview by replying to this email before 12:30 PM on 26th November 2025.",
    warmRegards: `Tiffany,
GKGS Careers Team
09036288119`,
    jobTitle: "Administrative Officer",
    reportsTo: "Studio Manager / Creative Director",
    jobSummary: "The purpose of this role is to support and ensure smooth daily operations within the studio. This role involves managing communication, scheduling, documentation, and coordination between creative, production, and client service teams. The ideal candidate should be detail-oriented, organized, and familiar with the media or creative industry environment.",
    keyResponsibilities: `• Administrative Support: Handle correspondence, emails, calls, and inquiries professionally.
• Scheduling & Coordination: Manage calendars, book appointments, schedule shoots/meetings, and coordinate studio usage.
• Client Relations: Greet clients and visitors, provide information about studio services, and maintain positive relationships.
• Documentation: Prepare and organize contracts, invoices, receipts, and production documents.
• Record Keeping: Maintain databases, contact lists, and digital archives of projects and media files.
• Financial Assistance: Support basic bookkeeping tasks such as expense tracking, petty cash, and payment follow-ups.
• Team Support: Assist the production and creative teams with logistics, supplies, and administrative tasks.
• Communication: Distribute internal memos, project updates, and assist with coordinating between departments.
• Office Management: Ensure the studio environment is tidy, stocked with supplies, and functioning efficiently.`,
    payRange: "80k - 100k"
  })

  // Use useMemo for filtered applicants to prevent unnecessary recalculations
  const filteredApplicants = useMemo(() => {
    return applicants.filter((applicant) => {
      const matchesSearch =
        applicant.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        applicant.email_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
        applicant.preferred_job_titles.toLowerCase().includes(searchQuery.toLowerCase()) ||
        applicant.specific_roles_experience.toLowerCase().includes(searchQuery.toLowerCase())

      let matchesStatusTab = true
      switch (statusTab) {
        case "screened":
          matchesStatusTab = applicant.screened_status === 1
          break
        case "unscreened":
          matchesStatusTab = applicant.unscreened_status === 1
          break
        case "interviewed":
          matchesStatusTab = applicant.interviewed_status === 1
          break
        case "employed":
          matchesStatusTab = applicant.employed_status === 1
          break
        case "rejected":
          matchesStatusTab = applicant.rejected_status === 1
          break
        case "all":
        default:
          matchesStatusTab = true
      }

      const matchesEducation = !filters.education || applicant.highest_education_level === filters.education
      const matchesLocationState = !filters.location_state || applicant.location_state_region.toLowerCase().includes(filters.location_state.toLowerCase())
      const matchesLocationCity = !filters.location_city || applicant.location_city.toLowerCase().includes(filters.location_city.toLowerCase())
      const matchesNationality = !filters.nationality || applicant.nationality.toLowerCase().includes(filters.nationality.toLowerCase())
      const matchesGender = !filters.gender || applicant.gender === filters.gender
      const matchesEmploymentType = !filters.employment_type || applicant.employment_type_preferences.includes(filters.employment_type)
      const matchesRemoteWork = !filters.remote_work || applicant.remote_work_preference.toString() === filters.remote_work
      const matchesWillingToRelocate = !filters.willing_to_relocate || applicant.willing_to_relocate.toString() === filters.willing_to_relocate
      const matchesPreferredIndustries = !filters.preferred_industries || applicant.preferred_industries.toLowerCase().includes(filters.preferred_industries.toLowerCase())
      const matchesAvailability = !filters.availability || applicant.availability.toLowerCase().includes(filters.availability.toLowerCase())

      return (
        matchesSearch &&
        matchesStatusTab &&
        matchesEducation &&
        matchesLocationState &&
        matchesLocationCity &&
        matchesNationality &&
        matchesGender &&
        matchesEmploymentType &&
        matchesRemoteWork &&
        matchesWillingToRelocate &&
        matchesPreferredIndustries &&
        matchesAvailability
      )
    })
  }, [applicants, searchQuery, filters, statusTab])

  const getApplicansts = async () => {
    try {
      const data = {
        project_id: projectId,
        jobrole_id: job_roleID,
      }
      const res = await getJobApplicantByJobRole(data)
      setApplicants(res.data)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    getApplicansts()
  }, [])

  const handleTabChange = (e: React.SyntheticEvent, newValue: StatusTab) => {
    setStatusTab(newValue)
    // Clear selected applicants when changing tabs to avoid confusion
    setSelectedApplicants([])
  }

  const handleSelectApplicant = (applicantId: number) => {
    setSelectedApplicants((prev) =>
      prev.includes(applicantId) ? prev.filter((id) => id !== applicantId) : [...prev, applicantId],
    )
  }

  const handleSelectAll = () => {
    if (selectedApplicants.length === filteredApplicants.length) {
      setSelectedApplicants([])
    } else {
      setSelectedApplicants(filteredApplicants.map((a) => a.user_id))
    }
  }

  const sendToEndpoint = async (qualified: boolean, statusTab: StatusTab) => {
    // If sending to interview from screened tab, show interview dialog
    if (statusTab === "screened" && qualified) {
      setShowInterviewDialog(true)
      return
    }

    const selectedData = applicants.filter(app => selectedApplicants.includes(app.user_id));

    const payload = {
      applicants: selectedData,
      qualified: qualified,
      project_id: projectId,
      job_role_id: job_roleID,
      action: statusTab,
      business_id: localStorage.getItem('business_id') // Get business_id from localStorage
    }

    try {
      setIsLoading(true)
      const response = await updateSelectedApplicants(payload)
      console.log({ response })

      setSnackbar({
        open: true,
        message: `Successfully sent ${selectedData.length} applicant(s) as ${
          qualified ? "qualified" : "not qualified"
        }`,
        severity: "success",
      })
      setSelectedApplicants([])
      getApplicansts()
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to send applicants. Please try again.",
        severity: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendToInterview = async () => {
    const selectedData = applicants.filter(app => selectedApplicants.includes(app.user_id));

    // Prepare the interview email content
    const emailContent = `
Dear Applicant,

Thank you for sharing your CV with us. After reviewing your application, we are pleased to invite you to the next stage of our recruitment process – an interview for the position of ${interviewDetails.jobTitle}.

JOB DETAILS:

Reports To: ${interviewDetails.reportsTo}

Job Summary:
${interviewDetails.jobSummary}

Key Responsibilities:
${interviewDetails.keyResponsibilities}

Working Conditions:
${interviewDetails.workingConditions}

Ideal Work Schedule:

1. Standard Hours:
• Monday – Friday: ${interviewDetails.standardHours.mondayToFriday}
• Break: ${interviewDetails.standardHours.break}
• Saturday (optional): ${interviewDetails.standardHours.saturday}

2. Flexibility:
${interviewDetails.flexibility}

Ideal Pay Range:
Pay varies based on experience, location: ${interviewDetails.payRange}

INTERVIEW DETAILS:

Date: ${interviewDetails.interviewDate}
Time: ${interviewDetails.interviewTime}

${interviewDetails.interviewType === 'physical' ? 
`Location: ${interviewDetails.address}` : 
`Zoom Link: ${interviewDetails.zoomLink}`}

Contact:
If you find difficulty ${interviewDetails.interviewType === 'physical' ? 'navigating the business location' : 'accessing the virtual meeting'}, kindly call the business owner on ${interviewDetails.contactInfo} for assistance.

RECOMMENDATION:
Kindly ensure to ${interviewDetails.interviewType === 'physical' ? 'arrive on time, be professionally dressed, smell good, respond to questions professionally, and endeavour to research the company ahead of time.' : 'join the meeting on time, be professionally dressed, and ensure you have a stable internet connection.'}

${interviewDetails.recommendation}

Warm regards,
${interviewDetails.warmRegards}
    `.trim()

    const payload = {
      applicants: selectedData,
      qualified: true,
      project_id: projectId,
      job_role_id: job_roleID,
      action: "screened", // This will move them to interviewed status
      interview_details: interviewDetails,
      email_content: emailContent,
      business_id: localStorage.getItem('business_id'), // Get business_id from localStorage
      interview_type: interviewDetails.interviewType,
      interview_date: interviewDetails.interviewDate,
      interview_time: interviewDetails.interviewTime,
      location: interviewDetails.interviewType === 'physical' ? interviewDetails.address : interviewDetails.zoomLink,
      contact_info: interviewDetails.contactInfo
    }

    try {
      setIsLoading(true)
      const response = await updateSelectedApplicants(payload)
      console.log({ response })

      setSnackbar({
        open: true,
        message: `Successfully scheduled interview for ${selectedData.length} applicant(s)`,
        severity: "success",
      })
      setSelectedApplicants([])
      setShowInterviewDialog(false)
      getApplicansts()
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to schedule interview. Please try again.",
        severity: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: number) => {
    return status === 1 ? "success" : "default"
  }

  const formatSalary = (min: string, max: string) => {
    return `₦${Number.parseInt(min).toLocaleString()} - ₦${Number.parseInt(max).toLocaleString()}`
  }

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const clearFilters = () => {
    setFilters({
      education: "",
      location_state: "",
      location_city: "",
      nationality: "",
      gender: "",
      employment_type: "",
      salary_min: "",
      salary_max: "",
      remote_work: "",
      willing_to_relocate: "",
      preferred_industries: "",
      availability: "",
    })
    setSearchQuery("")
    setStatusTab("unscreened")
  }

  const getTabCounts = () => {
    const allCount = applicants.length
    const screenedCount = applicants.filter((app) => app.screened_status === 1).length
    const unscreenedCount = applicants.filter((app) => app.unscreened_status === 1).length
    const interviewedCount = applicants.filter((app) => app.interviewed_status === 1).length
    const employedCount = applicants.filter((app) => app.employed_status === 1).length
    const rejectedCount = applicants.filter((app) => app.rejected_status === 1).length

    return { allCount, screenedCount, unscreenedCount, interviewedCount, employedCount, rejectedCount }
  }

  const tabCounts = getTabCounts()

  const getActionButtonsForTab = () => {
    switch (statusTab) {
      case "unscreened":
        return {
          show: true,
          qualifiedLabel: "Send as Screened",
          notQualifiedLabel: "Reject",
        }
      case "screened":
        return {
          show: true,
          qualifiedLabel: "Send to Interview",
          notQualifiedLabel: "Reject",
        }
      case "interviewed":
        return {
          show: true,
          qualifiedLabel: "Send as Hired",
          notQualifiedLabel: "Reject",
        }
      case "employed":
        return {
          show: true, // Changed from false to true
          qualifiedLabel: "Restore to Interview", // Option to move back to interview stage
          notQualifiedLabel: "Reject Employment", // Option to reject employment
        }
      case "rejected":
        return {
          show: true,
          qualifiedLabel: "Restore Applicant",
          notQualifiedLabel: "Permanently Remove",
        }
      default:
        return {
          show: true,
          qualifiedLabel: "Send as Qualified",
          notQualifiedLabel: "Send as Not Qualified",
        }
    }
  }

  const actionButtons = getActionButtonsForTab()
  const getFileExtension = (filename: string) => {
    return filename.split('.').pop();
  };

  const handleDownloadCV = async (cvFileName: string) => {
    try {
      const cvUrl = `https://beninclub1931apps.com.ng/public/assets/img/gkgsportal_cv/${cvFileName}`;
      
      // Fetch the file as a blob
      const response = await fetch(cvUrl);
      const blob = await response.blob();
      
      // Create a blob URL
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', cvFileName);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CV:', error);
      // Fallback to opening in new tab if fetch fails
      window.open(`https://beninclub1931apps.com.ng/public/assets/img/gkgsportal_cv/${cvFileName}`, '_blank');
    }
  };

  const handlePreviewCV = (cvFileName: string) => {
    setPreviewCVFile(cvFileName);
    setShowCVPreview(true);
  };

  const handleInterviewTypeChange = (type: "physical" | "virtual") => {
    setInterviewDetails(prev => ({
      ...prev,
      interviewType: type
    }))
  }

  return (
    <IonPage style={{ background: "#ffffff" }} className="pushDown">
      <IonContent style={{ "--background": "#ffffff", background: "#ffffff" }}>
        <Box sx={{ p: 3, background: "#ffffff", minHeight: "100vh" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => history.goBack()}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => getApplicansts()}
              >
                Refresh
              </Button>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Applicant Tracking System
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and filter job applicants for project {projectId}
            </Typography>
          </Box>

          <Paper elevation={1} sx={{ mb: 3, background: "#ffffff" }}>
            <Tabs
              value={statusTab}
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label={`Unscreened (${tabCounts.unscreenedCount})`} value="unscreened" />
              <Tab label={`Screened (${tabCounts.screenedCount})`} value="screened" />
              <Tab label={`Interviewed (${tabCounts.interviewedCount})`} value="interviewed" />
              <Tab label={`Employed (${tabCounts.employedCount})`} value="employed" />
              <Tab label={`Rejected (${tabCounts.rejectedCount})`} value="rejected" />
            </Tabs>
          </Paper>

          {/* Search and Filter Bar */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, background: "#ffffff" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search applicants by name, email, or skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchQuery("")} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => setShowFilters(!showFilters)}>
                    Filters
                  </Button>
                  <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>
                    Clear All
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {showFilters && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Education</InputLabel>
                      <Select
                        value={filters.education}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            education: e.target.value,
                          }))
                        }
                        label="Education"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="BSc">BSc</MenuItem>
                        <MenuItem value="MSc">MSc</MenuItem>
                        <MenuItem value="PhD">PhD</MenuItem>
                        <MenuItem value="HND">HND</MenuItem>
                        <MenuItem value="Diploma">Diploma</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="State/Region"
                      value={filters.location_state}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          location_state: e.target.value,
                        }))
                      }
                      placeholder="e.g., Lagos, Abuja"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={filters.location_city}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          location_city: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nationality"
                      value={filters.nationality}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          nationality: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={filters.gender}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            gender: e.target.value,
                          }))
                        }
                        label="Gender"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Employment Type</InputLabel>
                      <Select
                        value={filters.employment_type}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            employment_type: e.target.value,
                          }))
                        }
                        label="Employment Type"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Full-Time">Full-Time</MenuItem>
                        <MenuItem value="Part-Time">Part-Time</MenuItem>
                        <MenuItem value="Contract">Contract</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Remote Work</InputLabel>
                      <Select
                        value={filters.remote_work}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            remote_work: e.target.value,
                          }))
                        }
                        label="Remote Work"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="1">Open to Remote</MenuItem>
                        <MenuItem value="0">No Remote</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Willing to Relocate</InputLabel>
                      <Select
                        value={filters.willing_to_relocate}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            willing_to_relocate: e.target.value,
                          }))
                        }
                        label="Willing to Relocate"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="1">Yes</MenuItem>
                        <MenuItem value="0">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Industries"
                      value={filters.preferred_industries}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          preferred_industries: e.target.value,
                        }))
                      }
                      placeholder="e.g., Tech, Finance"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Availability"
                      value={filters.availability}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          availability: e.target.value,
                        }))
                      }
                      placeholder="e.g., Immediate, 2 weeks"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Min Salary"
                      type="number"
                      value={filters.salary_min}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          salary_min: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Max Salary"
                      type="number"
                      value={filters.salary_max}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          salary_max: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Bulk Actions */}
          {selectedApplicants.length > 0 && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: "primary.light",
                color: "primary.contrastText",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">{selectedApplicants.length} applicant(s) selected</Typography>
                <Stack direction="row" spacing={2}>
                  {actionButtons.show && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => sendToEndpoint(true, statusTab)}
                        disabled={isLoading}
                      >
                        {actionButtons.qualifiedLabel}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => sendToEndpoint(false, statusTab)}
                        disabled={isLoading}
                      >
                        {actionButtons.notQualifiedLabel}
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
              {isLoading && <LinearProgress sx={{ mt: 1 }} />}
            </Paper>
          )}

          {/* Results Summary */}
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6">{filteredApplicants.length} applicant(s) found</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedApplicants.length === filteredApplicants.length && filteredApplicants.length > 0}
                  indeterminate={selectedApplicants.length > 0 && selectedApplicants.length < filteredApplicants.length}
                  onChange={handleSelectAll}
                />
              }
              label="Select All"
            />
          </Box>

          {/* Applicants Grid */}
          <Grid container spacing={3}>
            {filteredApplicants.map((applicant, i) => (
              <Grid item xs={12} md={6} lg={4} key={`${applicant.user_id}-${i}`}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 4,
                    },
                    border: selectedApplicants.includes(applicant.user_id) ? 2 : 0,
                    borderColor: "primary.main",
                    background: "#ffffff",
                  }}
                >
                  <CardContent>
                    {/* Header with checkbox and avatar */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Checkbox
                        checked={selectedApplicants.includes(applicant.user_id)}
                        onChange={() => handleSelectApplicant(applicant.user_id)}
                        sx={{ mr: 1 }}
                      />
                      <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>{applicant.full_name.charAt(0)}</Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {applicant.full_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {applicant.preferred_job_titles}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => {
                          setSelectedApplicant(applicant)
                          setShowApplicantModal(true)
                        }}
                        size="small"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Key Information */}
                    <Stack spacing={1.5}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{applicant.email_address}</Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {applicant.location_city}, {applicant.location_state_region}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <SchoolIcon fontSize="small" color="action" />
                        <Typography variant="body2">{applicant.highest_education_level}</Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <WorkIcon fontSize="small" color="action" />
                        <Typography variant="body2">{applicant.total_years_experience} years experience</Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MoneyIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatToNaira(applicant.salary_expectation_min)}-
                          {formatToNaira(applicant.salary_expectation_max)}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Status Chips */}
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      <Chip
                        label={applicant.interviewed_status ? "Interviewed" : "Not Interviewed"}
                        color={getStatusColor(applicant.interviewed_status)}
                        size="small"
                      />
                      <Chip
                        label={applicant.screened_status ? "Screened" : "Unscreened"}
                        color={getStatusColor(applicant.screened_status)}
                        size="small"
                      />
                      {applicant.employed_status === 1 && (
                        <Chip label="Employed" color="success" size="small" />
                      )}
                      {applicant.rejected_status === 1 && (
                        <Chip label="Rejected" color="error" size="small" />
                      )}
                      {applicant.remote_work_preference === 1 && <Chip label="Remote OK" color="info" size="small" />}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {filteredApplicants.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8, background: "#ffffff" }}>
              <PersonIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No applicants found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Try adjusting your search criteria or filters
              </Typography>
            </Box>
          )}

          {/* Applicant Detail Modal */}
          <Dialog
            open={showApplicantModal}
            onClose={() => setShowApplicantModal(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, background: "#ffffff" } }}
          >
            {selectedApplicant && (
              <>
                <DialogTitle sx={{ background: "#ffffff" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar sx={{ bgcolor: "primary.main" }}>{selectedApplicant.full_name.charAt(0)}</Avatar>
                    <Box>
                      <Typography variant="h6">{selectedApplicant.full_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedApplicant.preferred_job_titles}
                      </Typography>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ background: "#ffffff" }}>
                  <Grid container spacing={3}>
                    {/* Personal Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Personal Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.email_address}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.phone_number}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Age
                          </Typography>
                          <Typography variant="body1">
                            {calculateAge(selectedApplicant.date_of_birth)} years old
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Gender
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.gender}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Nationality
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.nationality}</Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Professional Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Professional Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Education
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.highest_education_level}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Experience
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.total_years_experience} years</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Specific Experience
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.specific_roles_experience}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Job Functions
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.job_functions}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Languages
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.languages_spoken}</Typography>
                        </Box>
                        
                        {/* CV File Section */}
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Curriculum Vitae
                          </Typography>
                          {selectedApplicant.cv_file ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DescriptionIcon />}
                                onClick={() => handleDownloadCV(selectedApplicant.cv_file)}
                                sx={{ textTransform: 'none' }}
                              >
                                Download CV
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handlePreviewCV(selectedApplicant.cv_file)}
                                sx={{ textTransform: 'none' }}
                              >
                                Preview
                              </Button>
                              <Chip 
                                label={getFileExtension(selectedApplicant.cv_file).toUpperCase()} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No CV uploaded
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Location & Preferences */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Location & Preferences
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary">
                            Location
                          </Typography>
                          <Typography variant="body1">
                            {selectedApplicant.location_area}, {selectedApplicant.location_city},{" "}
                            {selectedApplicant.location_state_region}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary">
                            Salary Expectation
                          </Typography>
                          <Typography variant="body1">
                            {formatSalary(
                              selectedApplicant.salary_expectation_min,
                              selectedApplicant.salary_expectation_max,
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary">
                            Availability
                          </Typography>
                          <Typography variant="body1">{selectedApplicant.availability}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Status Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Status
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label={selectedApplicant.interviewed_status ? "Interviewed" : "Not Interviewed"}
                          color={getStatusColor(selectedApplicant.interviewed_status)}
                        />
                        <Chip
                          label={selectedApplicant.screened_status ? "Screened" : "Unscreened"}
                          color={getStatusColor(selectedApplicant.screened_status)}
                        />
                        <Chip
                          label={selectedApplicant.employed_status ? "Currently Employed" : "Available"}
                          color={selectedApplicant.employed_status ? "warning" : "success"}
                        />
                        {selectedApplicant.rejected_status === 1 && (
                          <Chip label="Rejected" color="error" />
                        )}
                        {selectedApplicant.remote_work_preference === 1 && <Chip label="Remote Work OK" color="info" />}
                        {selectedApplicant.willing_to_relocate === 1 && (
                          <Chip label="Willing to Relocate" color="secondary" />
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ background: "#ffffff" }}>
                  <Button onClick={() => setShowApplicantModal(false)}>Close</Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      setSelectedApplicants([selectedApplicant.user_id])
                      setShowApplicantModal(false)
                    }}
                  >
                    Select for Processing
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Interview Scheduling Dialog */}
          <Dialog
            open={showInterviewDialog}
            onClose={() => setShowInterviewDialog(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, background: "#ffffff" } }}
          >
            <DialogTitle>
              <Typography variant="h6">Schedule Interview</Typography>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 2 }}>
               

                {/* Interview Type Selection */}
                <FormControl component="fieldset">
                  <FormLabel component="legend">Interview Type</FormLabel>
                  <RadioGroup
                    value={interviewDetails.interviewType}
                    onChange={(e) => handleInterviewTypeChange(e.target.value as "physical" | "virtual")}
                  >
                    <FormControlLabel value="physical" control={<Radio />} label="Physical Interview" />
                    <FormControlLabel value="virtual" control={<Radio />} label="Virtual Interview" />
                  </RadioGroup>
                </FormControl>

                {/* Physical Address */}
                {interviewDetails.interviewType === "physical" && (
                  <TextField
                    label="Interview Address"
                    value={interviewDetails.address}
                    onChange={(e) => setInterviewDetails(prev => ({ ...prev, address: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                  />
                )}

                {/* Zoom Link */}
                {interviewDetails.interviewType === "virtual" && (
                  <TextField
                    label="Zoom Link"
                    value={interviewDetails.zoomLink}
                    onChange={(e) => setInterviewDetails(prev => ({ ...prev, zoomLink: e.target.value }))}
                    fullWidth
                    placeholder="https://zoom.us/j/..."
                  />
                )}


 {/* Job Details */}
                <Box>
                  <Typography variant="h6" gutterBottom>Job Details</Typography>
                  <Grid container spacing={2}>
                 
                    <Grid item xs={12} sm={12}>
                      <TextField
                        label="Reports To"
                        value={interviewDetails.reportsTo}
                        onChange={(e) => setInterviewDetails(prev => ({ ...prev, reportsTo: e.target.value }))}
                        fullWidth
                      />
                    </Grid>
                
                    {/* <Grid item xs={12} sm={6}>
                      <TextField
                        label="Pay Range"
                        value={interviewDetails.payRange}
                        onChange={(e) => setInterviewDetails(prev => ({ ...prev, payRange: e.target.value }))}
                        fullWidth
                      />
                    </Grid> */}
                  </Grid>
                </Box>
                {/* Standard Working Hours */}
                <Box>
                  <Typography variant="h6" gutterBottom>Standard Working Hours</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Monday - Friday"
                        value={interviewDetails.standardHours.mondayToFriday}
                        onChange={(e) => setInterviewDetails(prev => ({
                          ...prev,
                          standardHours: { ...prev.standardHours, mondayToFriday: e.target.value }
                        }))}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Break"
                        value={interviewDetails.standardHours.break}
                        onChange={(e) => setInterviewDetails(prev => ({
                          ...prev,
                          standardHours: { ...prev.standardHours, break: e.target.value }
                        }))}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Saturday"
                        value={interviewDetails.standardHours.saturday}
                        onChange={(e) => setInterviewDetails(prev => ({
                          ...prev,
                          standardHours: { ...prev.standardHours, saturday: e.target.value }
                        }))}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Flexibility & Working Conditions */}
                <Box>
                  <Typography variant="h6" gutterBottom>Additional Details</Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Flexibility"
                      value={interviewDetails.flexibility}
                      onChange={(e) => setInterviewDetails(prev => ({ ...prev, flexibility: e.target.value }))}
                      fullWidth
                      multiline
                      rows={3}
                    />
                    <TextField
                      label="Working Conditions"
                      value={interviewDetails.workingConditions}
                      onChange={(e) => setInterviewDetails(prev => ({ ...prev, workingConditions: e.target.value }))}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Box>

                {/* Interview Schedule */}
                <Box>
                  <Typography variant="h6" gutterBottom>Interview Schedule</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Date"
                        value={interviewDetails.interviewDate}
                        onChange={(e) => setInterviewDetails(prev => ({ ...prev, interviewDate: e.target.value }))}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Time"
                        value={interviewDetails.interviewTime}
                        onChange={(e) => setInterviewDetails(prev => ({ ...prev, interviewTime: e.target.value }))}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Contact & Closing */}
                <Box>
                  <Typography variant="h6" gutterBottom>Contact & Closing</Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Contact Info"
                      value={interviewDetails.contactInfo}
                      onChange={(e) => setInterviewDetails(prev => ({ ...prev, contactInfo: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Recommendation"
                      value={interviewDetails.recommendation}
                      onChange={(e) => setInterviewDetails(prev => ({ ...prev, recommendation: e.target.value }))}
                      fullWidth
                      multiline
                      rows={2}
                    />
                    <TextField
                      label="Warm Regards"
                      value={interviewDetails.warmRegards}
                      onChange={(e) => setInterviewDetails(prev => ({ ...prev, warmRegards: e.target.value }))}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowInterviewDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSendToInterview}
                disabled={isLoading}
              >
                {isLoading ? <LinearProgress sx={{ width: 100 }} /> : "Send Invite"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  )
}

export default ATSJobApplicants